/**
 * Session 30.9 — Payment-aware table closing helpers.
 *
 *   isSessionPaid(session)     mirrors the backend `table_session_payment_state`
 *   verifyManagerPin(...)      online → POST /staff/manager-pin/verify
 *                              offline → bcrypt against the snapshot's
 *                              business.manager_pin_hash (same trick as
 *                              offline staff PIN login)
 *
 * Rule (all dashboards): paid table → close freely; unpaid table →
 * Manager/Void PIN required. "Paid" means a payment was actually
 * processed (or the table was moved to an account / owes nothing) —
 * having a receipt is NOT enough.
 */
import axios from "axios";
import bcrypt from "bcryptjs";
import { getBusiness } from "./localDb";

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

export function sessionPaymentState(session) {
  const s = session || {};
  const orders = Array.isArray(s.orders) ? s.orders : [];
  // ── Session 72 — ONE SOURCE OF TRUTH FOR MONEY ──────────────────────
  // Every session payload from the server carries the figures the server
  // itself uses to accept a tender and to allow "Close table":
  // amount_owed / paid_amount / outstanding_amount / fully_paid. When they
  // are present they ARE the answer — the local formula below is only for
  // optimistic / offline sessions that have not been reconciled yet.
  // (Three different formulas — chip, receipt, server — were disagreeing
  // on real tables: "Paid" on the chip, "Partially paid" on the receipt.)
  const srvOwed = Number(s.amount_owed);
  if (Number.isFinite(srvOwed) && typeof s.fully_paid === "boolean" && Number.isFinite(Number(s.paid_amount))) {
    const owed = Math.round(srvOwed * 100) / 100;
    const paidAmount = Math.round(Number(s.paid_amount) * 100) / 100;
    const outstanding = Number.isFinite(Number(s.outstanding_amount))
      ? Math.round(Number(s.outstanding_amount) * 100) / 100
      : Math.round(Math.max(0, owed - paidAmount) * 100) / 100;
    const movedToAccount = Boolean(s.moved_to_account_id);
    // outstanding (recomputed by the WS bill helper after every tender /
    // void) is the live figure; `fully_paid` is a snapshot stamp.
    const isPaid = movedToAccount || owed <= 0.009 || outstanding <= 0.009;
    return {
      isPaid,
      partiallyPaid: !isPaid && paidAmount > 0.009,
      feeNotCollected: isPaid ? num(s.service_fee_not_collected) : 0,
      total: Math.round(num(s.total_value) * 100) / 100,
      owed,
      paidAmount,
      outstanding: Math.max(outstanding, 0),
      recorded: Boolean(s.payment_recorded_at),
      movedToAccount,
      payments: Array.isArray(s.payments) ? s.payments : [],
      serverAuthoritative: true,
    };
  }
  let total = num(s.total_value);
  if (!total && orders.length) {
    total = orders.reduce((sum, o) => sum + (o.voided || o.status === "voided" ? 0 : num(o.total ?? o.subtotal)), 0);
  }
  total = Math.round(total * 100) / 100;
  // Session 31.26 — the multi-tender ledger: cash + every card + EFT.
  // A voucher genuinely reduces what is owed (mirrors the backend
  // `table_session_payment_state`).
  const voucher = num((s.applied_voucher || {}).discount_amount);
  const beforeFee = Math.round(Math.max(0, total - voucher) * 100) / 100;
  // Session 34/62 — the automatic service fee is part of what is owed.
  // The RULE (mirrors the server): fee = % × live items after voucher.
  // When the snapshot carries the % we always recompute from the current
  // base — a stored amount can be stale after a void (a 10% fee of a
  // voided R375 kept a fully-paid table on "R37.50 due"). The stored
  // amount is only used for legacy locks that have no %.
  const pct = s.service_fee_pct != null && Number.isFinite(Number(s.service_fee_pct)) ? num(s.service_fee_pct) : null;
  const storedFee = s.service_fee_amount != null && Number.isFinite(Number(s.service_fee_amount)) ? num(s.service_fee_amount) : null;
  let fee;
  if (pct != null && pct > 0) fee = Math.round(beforeFee * pct) / 100;
  else if (storedFee != null && (pct == null || s.service_fee_locked)) fee = storedFee;
  else fee = 0;
  if (beforeFee <= 0) fee = 0;
  const paidAmount = Math.round((num(s.cash_paid) + num(s.card_paid) + num(s.eft_paid)) * 100) / 100;
  // Session 62 — THE FEE FOLLOWS THE MONEY (mirrors the server payload):
  // once the payment covers the items, the bill is settled and the fee is
  // whatever was collected on top — never an "R88 due" the table chases.
  let feeNotCollected = 0;
  if (paidAmount > 0.009 && beforeFee > 0 && paidAmount >= beforeFee - 0.009 && paidAmount < Math.round((beforeFee + fee) * 100) / 100 - 0.009) {
    const collected = Math.round(Math.max(0, paidAmount - beforeFee) * 100) / 100;
    feeNotCollected = Math.round((fee - collected) * 100) / 100;
    fee = collected;
  }
  const owed = Math.round((beforeFee + fee) * 100) / 100;
  const outstanding = Math.round((owed - paidAmount) * 100) / 100;
  const recorded = Boolean(s.payment_recorded_at);
  const movedToAccount = Boolean(s.moved_to_account_id);
  const isPaid = movedToAccount || owed <= 0.009 || outstanding <= 0.009;
  const partiallyPaid = !isPaid && paidAmount > 0.009;
  return {
    isPaid,
    partiallyPaid,
    feeNotCollected,
    total,
    owed,
    paidAmount,
    outstanding: Math.max(outstanding, 0),
    recorded,
    movedToAccount,
    payments: Array.isArray(s.payments) ? s.payments : [],
  };
}

export function isSessionPaid(session) {
  return sessionPaymentState(session).isPaid;
}

function _backendUrl() {
  return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
}

/**
 * Verify the Manager/Void PIN. Returns { ok, offline, error }.
 * Never throws.
 */
export async function verifyManagerPin(pin, { token } = {}) {
  const clean = String(pin || "").trim();
  if (!clean) return { ok: false, offline: false, error: "Enter the manager PIN." };

  const online = typeof navigator === "undefined" ? true : navigator.onLine !== false;
  const realToken = token && !String(token).startsWith("offline:") ? token : null;

  if (online && realToken) {
    try {
      await axios.post(`${_backendUrl()}/api/staff/manager-pin/verify`, { manager_pin: clean }, {
        headers: { Authorization: `Bearer ${realToken}` },
        timeout: 8000,
      });
      return { ok: true, offline: false };
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403 || status === 401 || status === 400) {
        return { ok: false, offline: false, error: "Incorrect manager PIN." };
      }
      // Network problem → fall through to the local check.
    }
  }

  try {
    const biz = await getBusiness();
    const hash = biz && biz.manager_pin_hash;
    if (hash && typeof hash === "string") {
      const ok = await bcrypt.compare(clean, hash);
      return ok ? { ok: true, offline: true } : { ok: false, offline: true, error: "Incorrect manager PIN." };
    }
  } catch (_) { /* fall through */ }
  return { ok: false, offline: true, error: "Cannot verify the manager PIN right now (offline and no local copy). Reconnect and try again." };
}
